"use client";

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import styles from './_hcp-isi.module.scss';
import { LayoutContext } from "@/components/layout-context-container";
import Button from "@/components/button";
import CustomImage from "@/components/image";
import { lockScroll, unlockScroll } from "@/util/scrollLock";

export default function HcpIsi() {
  const { screenshotMode, isiHeight, setIsiHeight, isiHeightRef, isMobile, isiMobileStateCollapsed, setIsiMobileStateCollapsed, setAutoScrollActive } = useContext(LayoutContext);
  const router = useRouter();
  const { isReady } = router;
  const isiWrapperRef = useRef(null);
  const floatContainerRef = useRef(null);
  const isiRef = useRef(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [isiStateOpen, _setIsiStateOpen] = useState(false);
  const isiStateOpenRef = useRef(null);
  function setIsiStateOpen(val) {
    isiStateOpenRef.current = val;
    _setIsiStateOpen(val);
  }
  const [isInView, _setIsInView] = useState(false);
  const isInViewRef = useRef(isInView);
  function setIsInView(val) {
    isInViewRef.current = val;
    _setIsInView(val);
  }
  const [scrollToIsi, _setScrollToIsi] = useState(false);
  const scrollToIsiRef = useRef(scrollToIsi);
  function setScrollToIsi(val) {
    scrollToIsiRef.current = val;
    _setScrollToIsi(val);
  }
  const [isiExpanded, setIsiExpanded] = useState(false);

  function handleResize() {
    const height = isiWrapperRef.current?.getBoundingClientRect().height || 140;
    // console.log(isiStateOpenRef.current, height)
    setIsiHeight(height);
    floatContainerRef.current?.style.setProperty('--isi-collapsed-height', `${height}px`);
  }

  function handleScrollToIsi() {
    setScrollToIsi(true);
  }

  useEffect(() => {
    let travel = 0;

    function handleCollapse() {
      if (travel >= 10) {
        // show header after scrolling 10 pixels
        !scrollToIsiRef.current && setIsiMobileStateCollapsed(true);
        travel = 0;
      } else {
        travel++;
      }
    }
    
    handleResize();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    window.addEventListener('scroll', handleCollapse);
    () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      window.removeEventListener('scroll', handleCollapse);
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floatContainerRef.current]);

  useEffect(() => {
    // reset
    scrollToIsi && setAutoScrollActive(true);
    scrollToIsi && setTimeout(() => setScrollToIsi(false), 333);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToIsi])

  const hcpContentTextList = useMemo(() => [
    {
      header: <strong>INDICATION</strong>,
      body: <>CRENESSITY (crinecerfont) is indicated as adjunctive treatment to glucocorticoid replacement to control androgens in adults and pediatric patients 4 years of age and older with classic congenital adrenal hyperplasia (CAH).</>
    }, {
      header: (!isInView ? (<Button
        type="button"
        onClick={() => setIsiExpanded(false)}
      >
        <strong>IMPORTANT SAFETY INFORMATION</strong><CustomImage
          src="/images/components/hcp/isi/icon-collapse-isi.svg"
          alt=""
          width={11}
          height={11}
        />
      </Button>) : <strong>IMPORTANT SAFETY INFORMATION</strong>),
      subHeader: <>CONTRAINDICATIONS</>,
      body: <>CRENESSITY is contraindicated in patients with hypersensitivity to crinecerfont or any excipients of CRENESSITY.</>
    }, {
      subHeader: <strong>WARNINGS AND PRECAUTIONS</strong>
    }, {
      body: <><em><strong>Hypersensitivity Reactions.</strong></em> A hypersensitivity reaction, including throat tightness, angioedema, and generalized rash, occurred in a subject after 3 days of treatment with CRENESSITY. If a clinically significant hypersensitivity reaction occurs, initiate appropriate therapy and discontinue CRENESSITY.</>
    }, {
      body: <><em><strong>Risk of Acute Adrenal Insufficiency or Adrenal Crisis with Inadequate Concomitant Glucocorticoid Therapy.</strong></em> Acute adrenal insufficiency or adrenal crisis, which is potentially life-threatening, can occur in patients with underlying adrenal insufficiency who are on inadequate daily glucocorticoid doses, especially in situations associated with increased cortisol need, such as acute intercurrent illness, serious trauma, or surgical procedures. Continue glucocorticoids upon initiation of and during treatment with CRENESSITY. Do not reduce the glucocorticoid dose below the dose required for cortisol replacement. Patients should continue to use stress dosing of glucocorticoids in cases of increased cortisol need.</>
    }, {
      subHeader: <strong>ADVERSE REACTIONS</strong>
    }, {
      body: <>In adult patients, the most common adverse reactions (at least 4% for CRENESSITY and greater than placebo) are fatigue, headache, dizziness, arthralgia, back pain, decreased appetite, and myalgia.</>
    }, {
      body: <>In pediatric patients, the most common adverse reactions (at least 4% for CRENESSITY and greater than placebo) are headache, abdominal pain, fatigue, nasal congestion, and epistaxis.</>
    },  {
      body: <>You are encouraged to report negative side effects of prescription drugs to the FDA. Visit MedWatch at <Button
        customClass="!underline text-hcp-eggplant"
        href="https://www.fda.gov/medwatch"
        leaveSiteLink
        data-gtm-event-category="External Link"
        data-gtm-event-action="Click"
        data-gtm-event-label="www.fda.gov/medwatch"
      >www.fda.gov/medwatch</Button> or call <span className="text-nowrap"><a
        className="underline text-hcp-eggplant"
        href="tel:+18003321088"
        data-gtm-event-category="External Link"
        data-gtm-event-action="Click"
        data-gtm-event-label="1-800-FDA-1088"
      >1-800-FDA-1088</a>.</span></>
    }, {
      subHeader: <strong>Dosage Forms and Strengths:</strong>
    }, {
      body: <>CRENESSITY is available in 50 mg and 100 mg capsules, and as an oral solution of 50&nbsp;mg/mL.</>
    }
  ], [isInView]);

  const listItem = ({ header = null, subHeader = null, body = null }, i) => <>
    {header && <p className={`${styles.header}${i >= 3 ? ` ${styles['header--sub']}` : ''}`}>{header}</p>}
    {subHeader && <p className={styles['sub-header']}>{subHeader}</p>}
    {body && <p className={styles.body}>{body}</p>}
  </>;

  useEffect(() => {
    const element = isiRef.current;
    const containerHeight = floatContainerRef.current?.getBoundingClientRect().height || 1;
    const dynamicTreshold = Math.min(Math.max(isiHeightRef.current / containerHeight, 0), 1);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!screenshotMode) {
          const isiPositionY = isiRef.current?.getBoundingClientRect().y;
          if (isiPositionY > 0) {
            if (!isiStateOpenRef.current) {
              setIsInView(entry.isIntersecting);
            } else if (isiStateOpenRef.current && !entry.isIntersecting && isInViewRef.current) {
              setIsInView(false);
              setIsiStateOpen(false);
            }
          } else {
            setIsInView(true);
          }
        }
      }, {
        root: null, // defaults to the viewport
        rootMargin: "0px",
        threshold: !isiStateOpen ? 1 : dynamicTreshold,
      }
    );

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isiStateOpen]);

  useEffect(() => {
    setIsiStateOpen(isInView);
  }, [isInView]);

  useEffect(() => {
    if (!isiExpanded) return;
    lockScroll('isi');
    return () => {
      unlockScroll('isi');
    };
  }, [isiExpanded]);

  useEffect(() => {
    if (isReady) {
      isiRef.current?.style.setProperty('min-height', `${isiHeight}px`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isiHeight, isReady]);

  const floatContainerClass = useMemo(() => {
    let className = styles['float-container'];

    if (!isInView && !screenshotMode) {
      className = `${className} ${styles['float-container--fixed']}`;
    }

    if (isiStateOpen) {
      className = `${className} ${styles['float-container--open']}`;
    }

    if (isiExpanded) {
      className = `${className} ${styles['float-container--overlay']}`;
    }

    return className
  }, [isInView, isiStateOpen, screenshotMode, isiExpanded]);

  useEffect(() => {
    if (router.isReady) {
      let url = router.pathname;
      if (Object.keys(router.query).length > 0) {
        url = `${url}?${new URLSearchParams(
          Object.entries(router.query).reduce((acc, [key, value]) => {
            acc[key] = Array.isArray(value) ? value.join(',') : value || '';
            return acc;
          }, {})
        ).toString()}`;
      }
      setBaseUrl(url);
    }
  }, [router.isReady, router.pathname, router.query]);

  useEffect(() => {
    const hash = window.location.hash;
    let ctx = null;

    router.isReady && (async () => {
      if (hash.replace('#', '') === 'important-safety-information' && isiRef.current) {
        isiRef.current?.scrollIntoView({ block: "end", behavior: 'instant' });
      }

      const gsap = (await import('gsap')).default;
      const { ScrollToPlugin } = await import('gsap/ScrollToPlugin');

      gsap.registerPlugin(ScrollToPlugin);

      ctx = gsap.context(() => {
        if (hash.replace('#', '') === 'important-safety-information' && isiRef.current) {
          const y = isiRef.current?.getBoundingClientRect().top + window.scrollY;
          gsap.to(window, {duration: .33, scrollTo: {y: y}, ease: "linear", delay: 0.1});
        }
      });

      return () => {
        if (ctx) ctx.revert();
      };
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    const state = sessionStorage.getItem("isiMobileStateCollapsed");
    if(state === "true" ){
      setIsiMobileStateCollapsed(true);
    }else{
      setIsiMobileStateCollapsed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleResize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isiMobileStateCollapsed]);

  const mobileToggleClass = useMemo(() => isiMobileStateCollapsed ? `${styles.toggle} ${styles.collapsed}` : styles.toggle, [isiMobileStateCollapsed]);

  function toggleISI(){
    const newState = !isiMobileStateCollapsed;
    sessionStorage.setItem("isiMobileStateCollapsed", newState.toString());
    setIsiMobileStateCollapsed(newState);
  }

  return (
    <div className={styles.container} ref={isiRef} id="important-safety-information">
      <div className={floatContainerClass} ref={floatContainerRef}>
        {(isMobile && <button type="button" className={mobileToggleClass} onClick={toggleISI} />)}
        <div className={`container ${styles['isi-container']} ${styles['isi-container--collapsed']}`}>
          <div className={styles.wrapper} ref={isiWrapperRef}>
            <div className={`${styles.content} ${styles['content--collapsed']}`}>
              <div>
                {!isMobile && baseUrl && listItem({
                  body: (<>
                    <strong className="text-hcp-eggplant">INDICATION</strong> CRENESSITY (crinecerfont) is indicated as adjunctive treatment to glucocorticoid replacement to control androgens in adults and pediatric patients 4 years of age and older with classic congenital adrenal hyperplasia (CAH).
                  </>)
                }, 0)}
                {baseUrl && listItem({
                  header: (<>
                    <Button
                      type="button"
                      onClick={() => setIsiExpanded(true)}
                      data-gtm-event-category="Action"
                      data-gtm-event-action="Click"
                      data-gtm-event-label="Important Safety Information - Expand"
                      customClass="!underline"
                    >Please see full Important Safety Information<CustomImage
                      src="/images/components/hcp/isi/icon-expand-isi.svg"
                      alt=""
                      width={11}
                      height={11}
                    /></Button>
                  </>),
                  body: <>
                        <strong className="uppercase">Contraindications</strong> CRENESSITY is contraindicated in patients with hypersensitivity to crinecerfont or any excipients of CRENESSITY.
                      </>
                }, 0)}
              </div>
            </div>
          </div>
        </div>

        <div className={`container--small ${styles['isi-container']} ${styles['isi-container--expanded']}`}>
          <div className={`${styles.wrapper} ${styles['wrapper--expanded']}`}>
            <div>
              <div className={styles.content}>
                {
                  hcpContentTextList.map(({ header, subHeader, body }, i) => (
                    <React.Fragment key={`key_${i}`}>
                      {listItem({ header, subHeader, body }, i)}
                    </React.Fragment>
                  ))
                }
                <p>
                  <strong>
                    Please see full <Button
                      customClass="!underline text-hcp-eggplant"
                      targetBlank href="https://www.neurocrine.com/crenessitypi"
                      data-gtm-event-category="PDF"
                      data-gtm-event-action="Downlaod"
                      data-gtm-event-label="Prescribing Information"
                    >Prescribing Information</Button>.
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}